// utils/excelInventory.ts

import { InventoriesBetweenGet200ResponseRowsInner } from "@sizlcorp/sizl-api-document/dist/models";
import { TFunction } from "i18next";
import { headerStyle } from "./excelDownLoad";
import { rowData } from "./multiSheetExcelDownload";

export const mergeInventoryData = (data: InventoriesBetweenGet200ResponseRowsInner[]) => {
  const mergedMap = new Map();

  data.forEach((item) => {
    const key = [
      item.itemName ?? "",
      item.grade?.name ?? "",
      item.itemCategory?.name ?? "",
      item.abstractItem?.code ?? "",
    ].join("||");

    if (!mergedMap.has(key)) {
      mergedMap.set(key, {
        base: item,
        locationMap: new Map(),
      });
    }

    const group = mergedMap.get(key);
    const location = item.locationName ?? "";
    const qty = Number(item.quantityAtEnd) ?? 0;
    const prevQty = group.locationMap.get(location) ?? 0;

    group.locationMap.set(location, prevQty + qty);
  });

  return mergedMap;
};
const itemTypeOrder = {
  MATERIAL: 0,
  SUBSIDIARY: 1,
  SEMI_PRODUCT: 2,
  PRODUCT: 3,
};

export const sortMergedInventoryMap = (
  mergedMap: Map<
    string,
    { base: InventoriesBetweenGet200ResponseRowsInner; locationMap: Map<string, number> }
  >,
) => {
  return new Map(
    Array.from(mergedMap.entries()).sort(([_, a], [__, b]) => {
      const aOrder = itemTypeOrder[a.base.itemType as keyof typeof itemTypeOrder] ?? 99;
      const bOrder = itemTypeOrder[b.base.itemType as keyof typeof itemTypeOrder] ?? 99;

      // 같은 품목군이면 품목명 기준 정렬
      if (aOrder === bOrder) {
        return (a.base.itemName ?? "").localeCompare(b.base.itemName ?? "", "ko");
      }

      return aOrder - bOrder;
    }),
  );
};

export const generateExcelRows = (
  mergedMap: Map<any, any>,
  locationList: string[],
  t: TFunction<"translation", undefined>,
  style: any,
) => {
  return Array.from(
    mergedMap.values() as unknown as {
      base: InventoriesBetweenGet200ResponseRowsInner;
      locationMap: Map<string, number>;
    }[],
  ).map(({ base, locationMap }) => {
    const getItemTypeLabel = (itemType: string | undefined, t: TFunction) => {
      switch (itemType) {
        case "MATERIAL":
          return t("원자재");
        case "SUBSIDIARY":
          return t("부자재");
        case "SEMI_PRODUCT":
          return t("반제품");
        case "PRODUCT":
          return t("제품");
        default:
          return t(itemType ?? "");
      }
    };

    const basicInfo = [
      { v: getItemTypeLabel(base.itemType, t), t: "s", s: style },
      { v: base.itemName ?? "", t: "s", s: style },
      { v: base.grade?.name ?? "", t: "s", s: style },
      { v: base.itemCategory?.name ?? "", t: "s", s: style },
      { v: base.abstractItem?.code ?? "", t: "s", s: style },
    ];

    const locationCells = locationList.map((loc) => {
      const value = locationMap.get(loc) ?? 0;
      const isZero = value === 0;
      return {
        v: isZero ? "-" : value,
        t: isZero ? "s" : "n",
        s: {
          ...style,
          alignment: { horizontal: "right" },
          numFmt: isZero ? undefined : "#,##0",
        },
      };
    });

    const totalValue = locationList.reduce((sum, loc) => {
      const val = locationMap.get(loc) ?? 0;
      return sum + val;
    }, 0);

    const totalCell = {
      v: totalValue === 0 ? "-" : totalValue,
      t: totalValue === 0 ? "s" : "n",
      s: {
        ...style,
        alignment: { horizontal: "right" },
        numFmt: totalValue === 0 ? undefined : "#,##0",
      },
    };

    const row = [...basicInfo, totalCell, ...locationCells];

    return row;
  });
};

// 총합 계산
export const calculateColumnTotals = (
  dataRows: { v: number | string }[][],
  locationCount: number,
) => {
  const totals = new Array(locationCount + 1).fill(0);

  for (const row of dataRows) {
    // row[5] = 소계
    const totalCellValue = row[5]?.v;
    totals[0] += typeof totalCellValue === "number" ? totalCellValue : 0;

    for (let i = 0; i < locationCount; i++) {
      const cellValue = row[6 + i]?.v;
      totals[i + 1] += typeof cellValue === "number" ? cellValue : 0;
    }
  }

  return totals;
};

// 엑셀 헤더 스타일
export const createSummaryRow = (totals: number[], label: string) => {
  return [
    { v: label, t: "s", s: headerStyle }, // A
    { v: "", t: "s", s: headerStyle }, // B
    { v: "", t: "s", s: headerStyle }, // C
    { v: "", t: "s", s: headerStyle }, // D
    { v: "", t: "s", s: headerStyle }, // E
    { v: totals[0], t: "n", s: { ...headerStyle, numFmt: "#,##0" } },
    ...totals.slice(1).map((v) => ({
      v,
      t: "n",
      s: { ...headerStyle, numFmt: "#,##0" },
    })),
  ];
};

export const calculateMergesForItemNameColumn = (rows: any) => {
  const merges = [];
  let prevValue = null;
  let startRow = 2;

  for (let i = 0; i < rows.length; i++) {
    const currValue = rows[i][1]?.v;

    if (currValue !== prevValue) {
      if (i > 0 && i + 1 > startRow) {
        merges.push(`B${startRow}:B${i + 1}`);
      }
      startRow = i + 2;
      prevValue = currValue;
    } else if (i === rows.length - 1 && i + 2 > startRow) {
      merges.push(`B${startRow}:B${i + 2}`);
    }
  }

  return merges;
};

// 로케이션 가져오기
export const getLocationList = (data: InventoriesBetweenGet200ResponseRowsInner[]) => {
  return Array.from(new Set(data.map((item) => item.locationName ?? ""))).sort((a, b) => {
    const isAEnglish = /^[A-Za-z]/.test(a);
    const isBEnglish = /^[A-Za-z]/.test(b);
    if (isAEnglish && !isBEnglish) return -1;
    if (!isAEnglish && isBEnglish) return 1;
    return a.localeCompare(b, "ko");
  });
};

// 총합 셀 병합
export const addSummaryRowMerge = (rows: any, merges: string[]) => {
  const rowIndex = rows.length + 1;
  merges.push(`A${rowIndex}:E${rowIndex}`);
};

// 엑셀 시트 생성
export const createInventorySheet = (
  sheetName: string,
  locationList: string[],
  dataRows: rowData[][],
  merges: string[],
) => {
  return {
    sheetName,
    headerRow: [
      { v: "품목군", t: "s", s: headerStyle },
      { v: "품목 명", t: "s", s: headerStyle },
      { v: "Grade", t: "s", s: headerStyle },
      { v: "Color", t: "s", s: headerStyle },
      { v: "형태", t: "s", s: headerStyle },
      { v: "소계", t: "s", s: headerStyle },
      ...locationList.map((loc) => ({ v: loc ?? "", t: "s", s: headerStyle })),
    ],
    dataRows,
    merges,
  };
};
